// Key review — the "Review keys" step of database-creator's ConversionWizard, adapted to the
// project-scoped, persistent workspace: proposals are key links on the server (KeyLink: a column
// that references a key, plain PK/FK — N3), accepted/rejected by a curator; a manual link is
// checked against the data by the server (key_infer.validate_foreign_key) before it is accepted.
// The evidence (matching name, values found, a contradiction) is the link's comment.
import { useState } from "react";
import { Badge, Btn, Field, Select } from "../../components/Shared";
import { Empty } from "../../components/Feedback";

const STATUS_COLOR = { proposed: "amber", accepted: "green", rejected: "gray" };

// Visually hidden, still read out: gives each Accept/Reject button a unique accessible name.
const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)",
                 whiteSpace: "nowrap" };
const mono = { fontFamily: "'JetBrains Mono', monospace", fontSize: 13, overflowWrap: "anywhere", minWidth: 0 };

function End({ end }) {
  return (
    <span style={{ display: "inline-flex", gap: 6, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
      <Badge color="blue">{end.study}</Badge>
      <span style={mono}>{end.table}.{end.field}</span>
    </span>
  );
}

// Identifier spaces (completed task of 2026-10-05 on colliding identifiers): a cross-study link joins two
// studies' identifiers. The data check flags it when equal keys disagree elsewhere; a curator
// confirms "same identifier space" or "different spaces" (then the link is not built).
const SPACE_LABEL = { same: "same identifier space", different: "different identifier spaces" };

export function SpaceChoice({ space, label, canManage, busy, onSpace, extra }) {
  return (
    <div data-space style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
      <Badge color={space === "same" ? "green" : space === "different" ? "blue" : "amber"}>
        {SPACE_LABEL[space] || "identifier space not confirmed"}
      </Badge>
      {canManage && (
        <>
          {space !== "same" && (
            <Btn variant="ghost" disabled={busy} onClick={() => onSpace("same")} style={{ padding: "4px 10px", fontSize: 12 }}>
              Same identifier space<span style={srOnly}> {label}</span>
            </Btn>
          )}
          {space !== "different" && (
            <Btn variant="secondary" disabled={busy} onClick={() => onSpace("different")} style={{ padding: "4px 10px", fontSize: 12 }}>
              Different spaces<span style={srOnly}> {label}</span>
            </Btn>
          )}
          {space && (
            <Btn variant="secondary" disabled={busy} onClick={() => onSpace(null)} style={{ padding: "4px 10px", fontSize: 12 }}>
              Not confirmed<span style={srOnly}> {label}</span>
            </Btn>
          )}
          {extra}
        </>
      )}
    </div>
  );
}

// flex + wrap, not a "1fr auto" grid: on a narrow screen (320 px) the buttons move below the link.
function LinkRow({ m, canManage, busy, onAction, onSpace }) {
  return (
    <li
      data-mapping={m.id}
      style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center",
               padding: "12px 0", borderTop: "1px solid #e5e7eb" }}
    >
      <div style={{ flex: "1 1 220px", minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <End end={m.subject} />
          <span aria-hidden="true" style={{ color: "#6b7280" }}>→</span><span style={srOnly}>references</span>
          <End end={m.object} />
          {m.cross_study && <Badge color="green">cross-study</Badge>}
          <Badge color={STATUS_COLOR[m.status] || "gray"}>{m.status}</Badge>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
          {m.proposed_by ? <>added by {m.proposed_by}</> : <>proposed by the portal</>}
          {m.comment && <> · {m.comment}</>}
          {m.decided_by && m.status !== "proposed" && <> · {m.status} by {m.decided_by}</>}
        </div>
        {m.cross_study && m.status !== "rejected" && (
          <SpaceChoice space={m.identifier_space} canManage={canManage} busy={busy}
                       label={`${m.subject.table}.${m.subject.field} → ${m.object.table}`}
                       onSpace={space => onSpace(m, space)} />
        )}
      </div>
      {canManage && (
        <div data-actions style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {m.status !== "accepted" && (
            <Btn variant="ghost" disabled={busy} onClick={() => onAction(m, "accept")}
                 style={{ padding: "6px 12px", fontSize: 13 }}>
              Accept<span style={srOnly}> {m.subject.table}.{m.subject.field} → {m.object.table}</span>
            </Btn>
          )}
          {m.status !== "rejected" && (
            <Btn variant="secondary" disabled={busy} onClick={() => onAction(m, "reject")}
                 style={{ padding: "6px 12px", fontSize: 13 }}>
              Reject<span style={srOnly}> {m.subject.table}.{m.subject.field} → {m.object.table}</span>
            </Btn>
          )}
          {/* A decided link goes back to proposed (POST .../mappings/{id}/reset). */}
          {m.status !== "proposed" && (
            <Btn variant="secondary" disabled={busy} onClick={() => onAction(m, "reset")}
                 style={{ padding: "6px 12px", fontSize: 13 }}>
              Reset<span style={srOnly}> {m.subject.table}.{m.subject.field} → {m.object.table}</span>
            </Btn>
          )}
        </div>
      )}
    </li>
  );
}

function ManualLink({ tables, busy, onAdd }) {
  const included = tables.filter(t => t.include);
  const [table, setTable] = useState("");
  const [column, setColumn] = useState("");
  const [refTable, setRefTable] = useState("");
  const src = included.find(t => t.name === table);
  const ref = included.find(t => t.name === refTable);
  const refPk = ref?.primary_key;
  const ready = src && column && ref && refPk && ref.name !== src.name;

  const submit = async e => {
    e.preventDefault();
    const subject = src.fields.find(f => f.name === column);
    const object = ref.fields.find(f => f.name === refPk);
    const ok = await onAdd(subject.column_id, object.column_id);
    if (ok) { setTable(""); setColumn(""); setRefTable(""); }
  };

  return (
    <form onSubmit={submit} aria-label="Add a link by hand"
          style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12,
                   alignItems: "end", marginTop: 16 }}>
      <Field label="Table">
        <Select options={included.map(t => t.name)} value={table}
                onChange={e => { setTable(e.target.value); setColumn(""); }} />
      </Field>
      <Field label="Column">
        <Select options={src ? src.fields.map(f => f.name) : []} value={column}
                onChange={e => setColumn(e.target.value)} disabled={!src} />
      </Field>
      <Field label="References table" hint={ref ? (refPk ? `key: ${refPk}` : "this table has no primary key") : undefined}>
        <Select options={included.filter(t => t.name !== table).map(t => t.name)} value={refTable}
                onChange={e => setRefTable(e.target.value)} />
      </Field>
      <div><Btn type="submit" disabled={!ready || busy}>Check and add</Btn></div>
    </form>
  );
}

export default function KeyReview({ ws, canManage, busy, onAction, onAdd, onSpace }) {
  const [showRejected, setShowRejected] = useState(false);
  const rejected = ws.mappings.filter(m => m.status === "rejected");
  const shown = ws.mappings.filter(m => showRejected || m.status !== "rejected");
  return (
    <div>
      {ws.mappings.length === 0 ? (
        <Empty>
          No key links yet.{canManage ? " Use “Find key links” to compare the tables of all studies." : ""}
        </Empty>
      ) : (
        <ul aria-label="Key links" style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {shown.map(m => <LinkRow key={m.id} m={m} canManage={canManage} busy={busy} onAction={onAction}
                                   onSpace={onSpace} />)}
        </ul>
      )}
      {rejected.length > 0 && (
        <button type="button" onClick={() => setShowRejected(s => !s)}
                style={{ marginTop: 8, background: "none", border: "none", padding: 0, cursor: "pointer",
                         color: "var(--dp-accent-dark)", fontSize: 13, fontFamily: "inherit" }}>
          {showRejected ? "Hide" : "Show"} {rejected.length} rejected link{rejected.length === 1 ? "" : "s"}
        </button>
      )}
      {canManage && ws.tables.length > 1 && <ManualLink tables={ws.tables} busy={busy} onAdd={onAdd} />}
    </div>
  );
}
